import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import { TokenService } from "src/app/auth/token.service";
import { PermissionsEnum } from "src/app/shared/Models/enums/PermissionsEnum";
import { DataConvertingService } from "src/app/shared/services/data-converting.service";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { PrintingService } from "src/app/shared/services/printing.service";
import { ToastService } from "src/app/shared/toast.service";
import { DocumentListItemDTO } from "../../models/documentListItemsDTO";
import { DocumentListService } from "../../services/documentList.service";
import { DocumentEditContactInfoComponent } from "../document-contact-info-modal/document-contact-info-modal.component";
import { TemplateLanguage } from "src/app/shared/Models/enums/TemplateLanguage.enum";
import { TemplateType } from "src/app/shared/Models/enums/TemplateType.enum";

@Component({
  selector: "app-document-preview",
  templateUrl: "./document-preview.component.html",
  styleUrls: ["./document-preview.component.scss"],
})
export class DocumentPreviewComponent implements OnInit, OnDestroy {
  @Input() documentListValidationItem: DocumentListValidationItem;
  @Output() closeDocumentPreview: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  print: EventEmitter<DocumentPrintResponse> = new EventEmitter<DocumentPrintResponse>();

  canEditContacts: boolean;
  canPrintDocuments: boolean;

  pdf: ArrayBuffer;
  filename: string;
  filetype: string;

  constructor(
    private readonly modalService: NgbModal,
    private readonly service: DocumentListService,
    private readonly printingService: PrintingService,
    private readonly dataService: DataConvertingService,
    private readonly toastService: ToastService,
    private readonly permissionsService: PermissionsService
  ) {}

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.canPrintDocuments = this.permissionsService.allowed(
      PermissionsEnum.PrintDocuments
    );
    this.canEditContacts =
      (this.documentListValidationItem.type == TemplateType.TSA ||
        this.documentListValidationItem.type == TemplateType.TSANOADMIN) &&
      this.permissionsService.allowed(PermissionsEnum.EditContacts);

    this.service
      .getDocumentData(this.documentListValidationItem)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          response.data.stream;
          this.dataService
            .blobToArrayBuffer(response.data)
            .pipe(first())
            .subscribe((x) => (this.pdf = x));
          this.filename = response.fileName;
          this.filetype = response.fileType;
        },
        (err) => {
          this.canPrintDocuments = false;
          this.canEditContacts = false;
          if (err.error?.validationErrors?.length)
            err.error.validationErrors.forEach((element) => {
              this.toastService.showErrorToast(
                element.title,
                element.description
              );
            });
          else if (err?.error?.detailedError?.innerException?.message.length)
            this.toastService.showErrorToast(
              $localize`:@@general.error:Something went wrong`,
              err.error.detailedError.innerException.message
            );
          else if (err?.message?.length)
            this.toastService.showErrorToast(
              $localize`:@@general.error:Something went wrong`,
              err.error.message
            );
          else
            this.toastService.showErrorToast(
              $localize`:@@general.error:Something went wrong`,
              $localize`:@@validation.document.geterror:Something went wrong while generating the document.`
            );

          err.error.errors.forEach((element) => {});
        }
      );
  }

  onClose() {
    this.closeDocumentPreview.emit();
  }

  onPrint() {
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    )
      this.toastService.showErrorToast(
        $localize`:@@general.error:Something went wrong`,
        $localize`:@@validation.document.unsupporteddevice:Generating the document on mobile device is not supported.`
      );
    else
      this.print.emit({
        ...this.documentListValidationItem,
        callBack: () => {
          const blob = new Blob([this.pdf]);
          this.printingService.printLab(blob, this.filename, this.filetype);
        },
      });
  }

  onOpenEditContactInfo() {
    const modalRef = this.modalService.open(DocumentEditContactInfoComponent, {
      size: "lg",
    });
    modalRef.componentInstance.documentItem =
      this.documentListValidationItem.documentItem;
  }
}

export interface DocumentListValidationItem {
  documentItem: DocumentListItemDTO;
  type: TemplateType;
  lang: TemplateLanguage;
}

export interface DocumentPrintResponse {
  documentItem: DocumentListItemDTO;
  type: TemplateType;
  lang: TemplateLanguage;
  callBack: () => void;
}
